import React from 'react';
import { bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import { requiredFieldArrayCheckbox } from '../../util/validators';
import {
  Form,
  Button,
  FieldCheckboxGroup,
  FieldTextInput
} from '../../components';
import { findOptionsForSelectFilter } from '../../util/search';
import config from '../../config';
import { DEVELOPMENT_SITUATIONS_FILTER_ID, OTHER_OPTION } from '../../marketplace-custom-config';

import css from './EditListingDevelopmentSituationsForm.module.css';

const otherOptionComponent = (
  <FieldTextInput
    id="developmentSituationsOther"
    name="developmentSituationsOther"
    className={css.otherInput}
    type="text"
  />
)

const EditListingDevelopmentSituationsFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={formRenderProps => {
      const {
        className,
        disabled,
        ready,
        handleSubmit,
        intl,
        invalid,
        pristine,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        filterConfig,
        values
      } = formRenderProps;

      const developmentSituationsRequiredMessage = intl.formatMessage({
        id: 'EditListingDevelopmentSituationsForm.developmentSituationsRequired',
      });

      const { updateListingError, createListingDraftError, showListingsError } = fetchErrors || {};
      const errorMessageUpdateListing = updateListingError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingDevelopmentSituationsForm.updateFailed" />
        </p>
      ) : null;

      // This error happens only on first tab (of EditListingWizard)
      const errorMessageCreateListingDraft = createListingDraftError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingDevelopmentSituationsForm.createListingDraftError" />
        </p>
      ) : null;

      const errorMessageShowListing = showListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingDevelopmentSituationsForm.showListingFailed" />
        </p>
      ) : null;

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress;
      const isOtherOptionSelected = values?.developmentSituations?.includes(OTHER_OPTION);
      const submitDisabled =
        invalid ||
        disabled ||
        submitInProgress ||
        (isOtherOptionSelected && !values.developmentSituationsOther) ||
        (!isOtherOptionSelected && !!values.developmentSituationsOther);
      const developmentSituationsOptions = findOptionsForSelectFilter(
        DEVELOPMENT_SITUATIONS_FILTER_ID,
        filterConfig
      );

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {errorMessageCreateListingDraft}
          {errorMessageUpdateListing}
          {errorMessageShowListing}

          <FieldCheckboxGroup
            id="developmentSituations"
            name="developmentSituations"
            className={css.developmentSituations}
            validate={requiredFieldArrayCheckbox(developmentSituationsRequiredMessage)}
            options={developmentSituationsOptions}
            twoColumns
            otherOptionKey={OTHER_OPTION}
            otherOptionComponent={otherOptionComponent}
          />

          <Button
            className={css.submitButton}
            type="submit"
            inProgress={submitInProgress}
            disabled={submitDisabled}
            ready={submitReady}
          >
            {saveActionMsg}
          </Button>
        </Form>
      );
    }}
  />
);

EditListingDevelopmentSituationsFormComponent.defaultProps = {
  className: null,
  fetchErrors: null,
  filterConfig: config.custom.filters,
};

EditListingDevelopmentSituationsFormComponent.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  filterConfig: propTypes.filterConfig,
};

export default compose(injectIntl)(EditListingDevelopmentSituationsFormComponent);
